
import { useState, useEffect } from "react";
import { BarChart, Activity, Users, UserMinus } from "lucide-react";
import { MetricCard } from "@/components/dashboard/MetricCard";
import type { Client } from "@/types";
import { calculateMetrics } from "@/utils/metricCalculations";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface DashboardMetricsProps {
  coachClients: Client[];
  timeFrame: 'week' | 'month' | 'year';
  onTimeFrameChange: (value: string) => void;
}

export function DashboardMetrics({ 
  coachClients, 
  timeFrame, 
  onTimeFrameChange 
}: DashboardMetricsProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [metricsData, setMetricsData] = useState({
    totalClients: 0,
    activePrograms: 0,
    newClients: {
      week: { value: 0, change: 0, trend: "up" as const, chartData: [] as number[] },
      month: { value: 0, change: 0, trend: "up" as const, chartData: [] as number[] },
      year: { value: 0, change: 0, trend: "up" as const, chartData: [] as number[] }
    },
    removedClients: {
      week: { value: 0, change: 0, trend: "down" as const, chartData: [] as number[] },
      month: { value: 0, change: 0, trend: "down" as const, chartData: [] as number[] },
      year: { value: 0, change: 0, trend: "down" as const, chartData: [] as number[] }
    },
    clientsChartData: [0, 0, 0, 0, 0, 0, 0],
    programsChartData: [0, 0, 0, 0, 0, 0, 0]
  });

  const handleNavigateToPrograms = () => {
    navigate('/programs');
  };

  useEffect(() => {
    let isMounted = true;
    
    const fetchMetrics = async () => {
      setIsLoading(true);
      try {
        console.log('Fetching metrics for', coachClients.length, 'clients');
        const calculatedMetrics = await calculateMetrics(coachClients);
        
        if (isMounted) {
          console.log('Setting metrics data:', calculatedMetrics);
          // Update the type to match what's returned from calculateMetrics
          type MetricsDataType = typeof metricsData;
          type CalculatedMetricsType = typeof calculatedMetrics;
          
          // Safe type assertion using a type that combines both types
          const safeMetrics = calculatedMetrics as unknown as MetricsDataType;
          setMetricsData(safeMetrics);
        }
      } catch (error) {
        console.error('Error calculating metrics:', error);
        if (isMounted) {
          toast({
            title: "Error loading metrics",
            description: "There was a problem loading your dashboard data. Please try again.",
            variant: "destructive",
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchMetrics();
    
    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isMounted = false;
    };
  }, [coachClients, toast]); // Add toast to dependencies

  const metrics = [
    {
      label: "Total Clients",
      value: metricsData.totalClients,
      change: metricsData.totalClients > 0 ? metricsData.newClients[timeFrame].change : 0,
      trend: "up" as const,
      icon: Users,
      chartData: metricsData.clientsChartData,
      isLoading,
      onClick: () => navigate('/clients')
    },
    {
      label: "Active Programs",
      value: metricsData.activePrograms,
      change: metricsData.activePrograms > 0 ? 
        Math.round((metricsData.activePrograms / Math.max(metricsData.totalClients, 1)) * 100) : 0,
      trend: "up" as const,
      icon: Activity,
      chartData: metricsData.programsChartData,
      isLoading,
      onClick: handleNavigateToPrograms
    },
  ];

  const newClientsMetric = {
    label: "New Clients",
    ...metricsData.newClients[timeFrame],
    timeFrames: ['week', 'month', 'year'] as const,
    currentTimeFrame: timeFrame,
    onTimeFrameChange,
    icon: BarChart,
    chartData: metricsData.newClients[timeFrame].chartData,
    isLoading,
    onClick: () => navigate('/clients')
  };

  const removedClientsMetric = {
    label: "Removed Clients",
    ...metricsData.removedClients[timeFrame],
    timeFrames: ['week', 'month', 'year'] as const,
    currentTimeFrame: timeFrame,
    onTimeFrameChange,
    icon: UserMinus,
    chartData: metricsData.removedClients[timeFrame].chartData,
    isLoading,
    onClick: () => navigate('/clients')
  };

  // Custom layout with removed clients directly under new clients
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {metrics.map((metric) => (
        <MetricCard key={metric.label} metric={metric} />
      ))}
      <div className="flex flex-col gap-6">
        <MetricCard metric={newClientsMetric} />
        <MetricCard metric={removedClientsMetric} />
      </div>
    </div>
  );
}
